/* eslint-disable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content */

import { EMPTY_STRING } from "@regrello/core-utils";

import React from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

// (clewis): Hoist remarkPlugins to a stable module-level array. This is quite important: if the
// array is created inline in the JSX (e.g., `remarkPlugins={[remarkGfm]}`), a new reference is
// produced on every render, which causes react-markdown to re-initialize its entire plugin
// pipeline — including the expensive remark-gfm parser — even when the content hasn't changed.
const REMARK_PLUGINS = [remarkGfm];

// TODO: Would be nice to move this to a Tailwind @apply,
// see https://gitlab.com/regrello/action-items-prototype/-/merge_requests/14098#note_2454093461
const markdownRendererComponents: Components = {
  table: ({ node: _node, ...props }) => (
    // Wrap the table in a div to allow horizontal scrolling.
    <div className="overflow-x-auto w-full">
      <table className="min-w-full w-auto mt-2 border" {...props} />
    </div>
  ),
  tr: ({ node: _node, ...props }) => <tr className="border-b " {...props} />,
  th: ({ node: _node, ...props }) => (
    <td className="text-left font-semibold text-xs py-2.5 px-2 bg-backgroundSoft whitespace-nowrap" {...props} />
  ),
  td: ({ node: _node, ...props }) => <td className="py-2.5 text-xs px-2 whitespace-nowrap" {...props} />,
  h1: ({ node: _node, ...props }) => {
    return <h1 className="text-lg font-bold" {...props} />;
  },
  h2: ({ node: _node, ...props }) => {
    return <h2 className="text-base font-bold" {...props} />;
  },
  h3: ({ node: _node, ...props }) => {
    return <h3 className="text-sm font-bold" {...props} />;
  },
  h4: ({ node: _node, ...props }) => {
    return <h4 className="text-xs font-bold" {...props} />;
  },
  h5: ({ node: _node, ...props }) => {
    return <h5 className="text-xs font-bold" {...props} />;
  },
  h6: ({ node: _node, ...props }) => {
    return <h6 className="text-xs font-bold" {...props} />;
  },
  p: ({ node: _node, ...props }) => <p className="mb-2" {...props} />,
  a: ({ node: _node, ...props }) => <a className="text-primary-textMuted" {...props} />,
  blockquote: ({ node: _node, ...props }) => (
    <blockquote className="ml-4 border-l-4 border-neutral-border" {...props} />
  ),
  code: ({ node: _node, ...props }) => <code className="bg-neutral-soft px-1 rounded" {...props} />,
  pre: ({ node: _node, ...props }) => <pre className="bg-neutral-soft p-2 rounded" {...props} />,
  ul: ({ node: _node, ...props }) => <ul className="list-disc ml-6" {...props} />,
  ol: ({ node: _node, ...props }) => <ol className="list-decimal ml-6" {...props} />,
  li: ({ node: _node, ...props }) => <li className="mb-1" {...props} />,
};

/**
 * Error boundary component specifically for catching Markdown rendering errors
 */
class MarkdownErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  override render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

/**
 * A safe wrapper for Markdown that falls back to plain text if rendering fails.
 */
export const SafeMarkdown = ({ children }: { children: React.ReactNode }) => {
  // Extract the actual string content from children
  const extractStringContent = (): string => {
    // If it's an array (which is common with React children), try to extract the string
    if (Array.isArray(children) && children.length > 0) {
      const firstChild = children[0];
      if (typeof firstChild === "string") {
        return firstChild;
      }
    }

    // Last resort: try to convert to string
    try {
      return String(children);
    } catch (_) {
      // If conversion fails, return empty string
      return EMPTY_STRING;
    }
  };

  // Get the string content
  const stringContent = extractStringContent();

  // Store the content to use as fallback if rendering fails
  const fallbackContent = <>{stringContent}</>;

  // Use the error boundary to catch any rendering errors
  return (
    <MarkdownErrorBoundary fallback={fallbackContent}>
      <Markdown components={markdownRendererComponents} remarkPlugins={REMARK_PLUGINS}>
        {stringContent}
      </Markdown>
    </MarkdownErrorBoundary>
  );
};
